import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, X } from "lucide-react";

import { cn } from "@/lib/utils";
import { useGatewayStore } from "@/stores/useGatewayStore";
import { getOpenclawConfig, type OrgOpenclawConfig } from "@/api/lx-openclaw-config";
import { assistantNameViolation, formatAssistantAlias } from "@/lib/contacts-logic";
import type { AssistantIdentity } from "@/types/contacts";
import { AssistantAvatar } from "@/components/agent/AssistantAvatar";
import {
  ALEX_AVATARS,
  ALEX_AVATAR_TOKENS,
  type AlexAvatarToken,
} from "@/components/agent/assistant-avatars/AlexAvatars";
import { ASSISTANT_DEFAULT_NAME, assistantDisplayName } from "@/hooks/useAssistantIdentity";

/**
 * `AssistantIdentityDialog` — the editors behind the hero's identity pencils
 * (redesign §3.3 avatar picker + §4.1 inline rename, operator 2026-07-27:
 * the avatar pencil picks a face, the name pencil edits the name).
 *
 * `mode="avatar"` — a curated grid: the in-repo Alex originals (stored as a
 * short TOKEN, C1 — `avatar` is capped at 500 chars, so never a data-URI) and
 * an emoji row (the separate `emoji` field, avatar cleared).
 * `mode="name"` — a single field with the D-9 `assistantNameViolation`
 * precheck and "Alex" as the placeholder. Rejections come back as
 * `INVALID_PARAMS` with a readable reason (runtime `2f7a137f7`), not the old
 * opaque INTERNAL_ERROR.
 *
 * `ownerName` is REQUIRED for correctness, not polish. A live probe here on
 * 2026-07-27 found `identity.set` enforcing only the counsel-styling + 60-char
 * half of D-9 and ACCEPTING a real person's name — while this dialog passed an
 * empty `takenNames`, so the "never a real person" half had no enforcement on
 * this path at all and a lawyer could name the assistant after themselves. The
 * runtime half was closed same-day (`2f4df344a`, refuses any directory-contact
 * match, trim + case-insensitive; ships on the next image roll), so the two
 * sides now agree — but this precheck stays the primary gate: it is the only
 * one that speaks pt-BR and fires before a round trip.
 *
 * Both write through `identity.set` as a READ-MODIFY-WRITE echoing the FULL
 * editable block (C3: whole-record replace — omitting a field CLEARS it), the
 * pattern `IdentityLayout` already uses.
 */

/** A small, institutional set — professional marks, never real-person faces. */
const EMOJI_CHOICES = ["⚖️", "🦉", "🛡️", "✒️", "📚", "🗝️", "🧭", "🏛️"] as const;

/** C1: `avatar` is capped at 500 chars by the runtime schema — a custom URL
 *  longer than that would be rejected server-side with an opaque error, so it
 *  is refused here with a reason instead. */
const AVATAR_MAX = 500;

export function AssistantIdentityDialog({
  mode,
  assistant,
  ownerName,
  onClose,
  onChanged,
}: {
  mode: "avatar" | "name";
  assistant: AssistantIdentity | null;
  /** The §6.1 owner's display name — D-9's "never a real person's name" half,
   *  which `IdentityLayout` already enforced and `identity.set` did not until
   *  `2f4df344a`. */
  ownerName?: string;
  onClose: () => void;
  onChanged?: () => void;
}) {
  const { t } = useTranslation();
  const rpc = useGatewayStore((s) => s.rpc);

  // D-3 (the org lock on assistant identity) was DROPPED 2026-07-27 by operator
  // ruling: "agent identity is in the user's discretion". The
  // `userSettingsPolicy` mechanism stays — it still governs voice / browser /
  // push / personaSuffix / channels — but `agentIdentity` is no longer one of
  // its keys, so there is no lock to honour here. The org config is still read
  // for `agents.defaultIdentity`, which the avatar picker offers as a choice.
  const [org, setOrg] = useState<OrgOpenclawConfig | null>(null);
  useEffect(() => {
    let cancelled = false;
    getOpenclawConfig()
      .then((cfg) => !cancelled && setOrg(cfg.org ?? null))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);
  const orgDefault = org?.agents?.defaultIdentity;

  const [name, setName] = useState(() => assistantDisplayName(assistant));
  const [avatarToken, setAvatarToken] = useState<string | undefined>(assistant?.avatar);
  const [emoji, setEmoji] = useState<string | undefined>(assistant?.emoji);
  /** A custom image URL — the same `avatar` field, so picking a builtin or an
   *  emoji clears it and vice versa (whole-record semantics, C3). */
  const [customUrl, setCustomUrl] = useState(
    assistant?.avatar && /^https?:/i.test(assistant.avatar) ? assistant.avatar : "",
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Esc closes THIS dialog only. Capture-phase + stopImmediatePropagation so
  // FocusPaneHost's window Esc→closePane never also fires — without it one Esc
  // closed the dialog AND the whole pane underneath it.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopImmediatePropagation();
      onClose();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose]);

  const violation =
    mode === "name" ? assistantNameViolation(name, ownerName ? [ownerName] : []) : null;
  const urlTooLong = customUrl.trim().length > AVATAR_MAX;
  /** A typed URL wins over a previously-picked token — one field, one value. */
  const effectiveAvatar = customUrl.trim() ? customUrl.trim() : avatarToken;

  async function save() {
    if (!rpc || !assistant || saving) return;
    if (violation || urlTooLong) return;
    setSaving(true);
    setError(null);
    try {
      // FULL editable block echoed; only the field this dialog owns changes.
      // The name is always MATERIALIZED as the display name: for a
      // legacy-default record ("Lexiun") an avatar-mode save writes "Alex" —
      // otherwise the default-identity branch of the avatar resolver would
      // keep overriding the emoji the user just picked (preview showed it,
      // the hero would not). Also the §9.3 direction: engaging with identity
      // settles the default onto Alex.
      await rpc.identitySet({
        assistant: {
          name: mode === "name" ? name.trim() : assistantDisplayName(assistant),
          theme: assistant.theme,
          emoji: mode === "avatar" ? (customUrl.trim() ? undefined : emoji) : assistant.emoji,
          avatar: mode === "avatar" ? effectiveAvatar : assistant.avatar,
          emails: assistant.emails,
          emailDisplayName: assistant.emailDisplayName,
        },
      });
      onChanged?.();
      onClose();
    } catch (err) {
      // A rule rejection arrives as INVALID_PARAMS carrying a readable reason
      // rather than a ZodError JSON dump (2f7a137f7), so the raw message is
      // safe to show. It is still English, so the pt-BR precheck above stays
      // the primary gate — this is the defensive tail, not the normal path.
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  /** Preview identity so the grid shows what each choice will actually look
   *  like through the real resolver, not an approximation. */
  const preview: AssistantIdentity | null = assistant
    ? {
        ...assistant,
        name: name.trim() || ASSISTANT_DEFAULT_NAME,
        avatar: effectiveAvatar,
        emoji: customUrl.trim() ? undefined : emoji,
      }
    : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      data-testid={`assistant-identity-dialog-${mode}`}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t(mode === "avatar" ? "agent.hero.identity.avatarTitle" : "agent.hero.identity.nameTitle")}
        className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-5 shadow-xl dark:bg-coffee-900"
      >
        <div className="mb-1 flex items-start gap-2">
          <h2 className="min-w-0 flex-1 font-serif-display text-lg text-coffee-900 dark:text-cream-100">
            {t(mode === "avatar" ? "agent.hero.identity.avatarTitle" : "agent.hero.identity.nameTitle")}
          </h2>
          <button
            type="button"
            data-testid="assistant-identity-dialog-close"
            onClick={onClose}
            aria-label={t("common.close")}
            title={t("common.close")}
            className="shrink-0 rounded-lg p-1.5 text-coffee-500 transition-colors hover:bg-cream-100 hover:text-coffee-800 dark:text-cream-500 dark:hover:bg-coffee-800 dark:hover:text-cream-100"
          >
            <X size={16} />
          </button>
        </div>
        <p className="mb-4 text-body-sm text-coffee-600 dark:text-cream-400">
          {t(mode === "avatar" ? "agent.hero.identity.avatarBlurb" : "agent.hero.identity.nameBlurb")}
        </p>

        {mode === "avatar" ? (
          <div className="space-y-4">
            <div className="flex items-center gap-3 rounded-lg border border-cream-200/70 bg-cream-50/60 p-3 dark:border-coffee-800 dark:bg-coffee-950/40">
              <AssistantAvatar identity={preview} size={48} />
              <span className="truncate font-serif-display text-display-sm text-coffee-900 dark:text-cream-100">
                {preview ? assistantDisplayName(preview) : ASSISTANT_DEFAULT_NAME}
              </span>
            </div>

            <div>
              <span className="mb-1.5 block text-[10px] font-semibold uppercase tracking-wide text-coffee-500 dark:text-cream-500">
                {t("agent.hero.identity.suggestions")}
              </span>
              <div className="flex flex-wrap gap-2">
                {ALEX_AVATAR_TOKENS.map((token: AlexAvatarToken) => {
                  const Art = ALEX_AVATARS[token];
                  const selected = avatarToken === token && !emoji;
                  return (
                    <button
                      key={token}
                      type="button"
                      data-testid={`assistant-avatar-choice-${token.replace("builtin:", "")}`}
                      onClick={() => {
                        setAvatarToken(token);
                        setEmoji(undefined);
                        setCustomUrl("");
                      }}
                      aria-pressed={selected}
                      className={cn(
                        "rounded-full p-0.5 transition-all",
                        selected
                          ? "ring-2 ring-gold-500 ring-offset-2 ring-offset-white dark:ring-offset-coffee-900"
                          : "ring-1 ring-cream-200 hover:ring-cream-400 dark:ring-coffee-700 dark:hover:ring-coffee-600",
                      )}
                    >
                      <Art size={44} className="rounded-full" />
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <span className="mb-1.5 block text-[10px] font-semibold uppercase tracking-wide text-coffee-500 dark:text-cream-500">
                {t("agent.hero.identity.emoji")}
              </span>
              <div className="flex flex-wrap gap-2" data-testid="assistant-emoji-row">
                {EMOJI_CHOICES.map((e) => {
                  const selected = emoji === e;
                  return (
                    <button
                      key={e}
                      type="button"
                      data-testid={`assistant-emoji-choice-${e}`}
                      onClick={() => {
                        setEmoji(e);
                        // Whole-record semantics: an emoji face means no avatar.
                        setAvatarToken(undefined);
                        setCustomUrl("");
                      }}
                      aria-pressed={selected}
                      className={cn(
                        "flex h-11 w-11 items-center justify-center rounded-full bg-gold-400/20 text-xl transition-all dark:bg-gold-500/20",
                        selected
                          ? "ring-2 ring-gold-500 ring-offset-2 ring-offset-white dark:ring-offset-coffee-900"
                          : "ring-1 ring-cream-200 hover:ring-cream-400 dark:ring-coffee-700 dark:hover:ring-coffee-600",
                      )}
                    >
                      {e}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* PERSONALIZADO — a custom image URL (C1: stored verbatim in
                `avatar`, so it must fit the 500-char cap; never a data-URI). */}
            <div>
              <span className="mb-1.5 block text-[10px] font-semibold uppercase tracking-wide text-coffee-500 dark:text-cream-500">
                {t("agent.hero.identity.custom")}
              </span>
              <input
                type="url"
                value={customUrl}
                onChange={(e) => setCustomUrl(e.target.value)}
                placeholder="https://…"
                data-testid="assistant-avatar-url"
                className={cn(
                  "w-full rounded border bg-transparent px-2 py-1.5 text-sm",
                  urlTooLong ? "border-red-400" : "border-cream-300 dark:border-coffee-700",
                )}
              />
              {urlTooLong && (
                <p
                  className="mt-1 text-[11px] text-red-600 dark:text-red-300"
                  data-testid="assistant-avatar-url-error"
                >
                  {t("agent.hero.identity.customTooLong", { max: AVATAR_MAX })}
                </p>
              )}
              <p className="mt-1 text-[11px] text-coffee-500 dark:text-cream-500">
                {t("agent.hero.identity.customHint")}
              </p>
            </div>

            {/* PADRÃO DA ORGANIZAÇÃO — offered only when the org actually set
                one; an absent default is simply not shown, rather than an
                empty radio that promises something the org never configured
                (live-walk W-4: every org block is undefined today). */}
            {orgDefault?.avatarUrl && (
              <div>
                <span className="mb-1.5 block text-[10px] font-semibold uppercase tracking-wide text-coffee-500 dark:text-cream-500">
                  {t("agent.hero.identity.orgDefault")}
                </span>
                <button
                  type="button"
                  data-testid="assistant-avatar-org-default"
                  aria-pressed={avatarToken === orgDefault.avatarUrl}
                  onClick={() => {
                    setAvatarToken(orgDefault.avatarUrl);
                    setCustomUrl("");
                    setEmoji(undefined);
                  }}
                  className={cn(
                    "flex items-center gap-2 rounded-lg border px-2 py-1.5 transition-colors",
                    avatarToken === orgDefault.avatarUrl
                      ? "border-gold-500 bg-gold-400/10"
                      : "border-cream-300 hover:bg-cream-100 dark:border-coffee-700 dark:hover:bg-coffee-800",
                  )}
                >
                  <img
                    src={orgDefault.avatarUrl}
                    alt=""
                    className="h-8 w-8 rounded-full object-cover"
                  />
                  <span className="text-body-sm text-coffee-700 dark:text-cream-300">
                    {t("agent.hero.identity.orgDefaultUse")}
                  </span>
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            <input
              type="text"
              value={name}
              autoFocus
              onChange={(e) => setName(formatAssistantAlias(e.target.value))}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !violation) void save();
              }}
              placeholder={ASSISTANT_DEFAULT_NAME}
              data-testid="assistant-name-input"
              className={cn(
                "w-full rounded border bg-transparent px-3 py-2 text-body",
                violation ? "border-red-400" : "border-cream-300 dark:border-coffee-700",
              )}
            />
            {violation && (
              <p
                data-testid="assistant-name-violation"
                className="rounded bg-red-500/10 p-2 text-xs text-red-600 dark:text-red-300"
              >
                {violation}
              </p>
            )}
          </div>
        )}

        {error && (
          <p className="mt-3 rounded bg-red-500/10 p-2 text-xs text-red-600 dark:text-red-300">
            {error}
          </p>
        )}

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded px-3 py-1 text-body-sm text-coffee-600 transition-colors hover:bg-cream-100 dark:text-cream-400 dark:hover:bg-coffee-800"
          >
            {t("common.cancel", "Cancelar")}
          </button>
          <button
            type="button"
            onClick={() => void save()}
            disabled={
              saving ||
              !!violation ||
              urlTooLong ||
              !assistant ||
              (mode === "name" && !name.trim())
            }
            data-testid="assistant-identity-save"
            className="inline-flex items-center gap-1.5 rounded border border-gold-500/60 bg-gold-400/10 px-4 py-1 text-body-sm font-medium text-gold-700 transition-colors hover:bg-gold-400/25 disabled:opacity-50 dark:border-gold-500/40 dark:text-gold-400"
          >
            {saving && <Loader2 size={12} aria-hidden className="animate-spin" />}
            {t("common.save", "Salvar")}
          </button>
        </div>
      </div>
    </div>
  );
}
